import React, { ChangeEvent, useEffect, useState } from 'react';
import {
  Typography,
  IconButton,
  Grid,
  Divider,
  Backdrop,
  CircularProgress,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  SelectChangeEvent,
  ListItemText,
  OutlinedInput,
  FormHelperText,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import SaveIcon from '@mui/icons-material/Save';
import Cancel from '@mui/icons-material/Cancel';
import { ClientSecret, UserProfileMessage } from '../Model/ClientSecretesModel';
import { clientsecretService } from '../ClientSecretService';
import { useParams } from 'react-router-dom';
import CopyToClipboard from 'react-copy-to-clipboard';
import { ClientsSecreteToolbarProps } from '../Model/ClientsSecretesProps';
import { ThemedCopyIcon } from '../../../../Common/Components/Icons';
import HeaderToolbar from '../../../../Common/Components/HeaderToolbar';
import SearchBar from '../../../../Common/Components/Controls/SearchBar';
import CustomButton, {
  CustomTextField,
} from '../../../../Common/Components/Controls/InputControls';
import { CardWrapper } from '../../Common/CommonComponents';
import { useTranslation } from 'react-i18next';
import { successToastMessage } from '../CommonMethods/ClientSecretCommonMethods';
import { showToast } from '../../../../lib/toast';

interface Param {
  customerId: string;
}

interface FormErrors {
  description?: string;
  profile?: string;
}

export default function ClientsSecreteHeaderToolbar(
  props: Readonly<ClientsSecreteToolbarProps>,
) {
  const { onFilterRequest, customer, oncloseUpdateParent } = props;
  const { t } = useTranslation('clientsecret');
  const params = useParams<Param>();

  const [open, setOpen] = useState(false);

  const [description, setDescription] = useState('');
  const [selectedProfileId, setSelectedProfileId] = useState<string>('');
  const [errors, setErrors] = useState<FormErrors>({});

  const [profiles, setProfiles] = useState<UserProfileMessage[]>([]);
  const [profilesLoading, setProfilesLoading] = useState(false);

  const [generatedSecret, setGeneratedSecret] = useState<
    ClientSecret | undefined
  >();
  const [isSending, setIsSending] = useState(false);

  const loadProfiles = async () => {
    setProfilesLoading(true);
    try {
      const result = await clientsecretService.getAvailableProfiles(
        params.customerId,
        1,
      );
      setProfiles(result.Items ?? []);
    } catch (error) {
      console.error(error);
      showToast({
        title: t('clientsecret.error.profiles_load_failed'),
        type: 'error',
      });
    } finally {
      setProfilesLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      loadProfiles();
    }
  }, [open]);

  const validate = (): boolean => {
    const newErrors: FormErrors = {};

    if (!description.trim()) {
      newErrors.description = t('clientsecret.validation.description_required');
    }

    if (!selectedProfileId) {
      newErrors.profile = t('clientsecret.validation.profile_required');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleGenerate = async () => {
    if (!validate()) return;
    setIsSending(true);
    try {
      const result = await clientsecretService.storeClientSecret(
        params.customerId,
        {
          CustomerId: params.customerId,
          Description: description.trim(),
          ProfileId: selectedProfileId,
        },
      );
      setGeneratedSecret(result);
      showToast({
        title: t('clientsecret.success.apikey_generated'),
        type: 'success',
      });
    } catch (error) {
      console.error(error);
      showToast({
        title: t('clientsecret.error.generate_failed'),
        type: 'error',
      });
    } finally {
      setIsSending(false);
    }
  };

  const handleClickOpen = () => setOpen(true);

  const handleClose = () => {
    setOpen(false);
    setGeneratedSecret(undefined);
    setDescription('');
    setSelectedProfileId('');
    setProfiles([]);
    setErrors({});
    setIsSending(false);
    oncloseUpdateParent();
  };

  const handleDescriptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDescription(e.target.value);
    if (errors.description) {
      setErrors((prev) => ({ ...prev, description: undefined }));
    }
  };

  const handleProfileSelect = (event: SelectChangeEvent<string>) => {
    setSelectedProfileId(event.target.value);
    if (errors.profile) {
      setErrors((prev) => ({ ...prev, profile: undefined }));
    }
  };

  const handleSearchFilter = (event: ChangeEvent<HTMLInputElement>) => {
    onFilterRequest(event);
  };

  return (
    <>
      <HeaderToolbar
        leftContent={
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="h5" pl={1}>
                {t('clientsecret.header.title')} {customer.Subdomain}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <SearchBar onChange={handleSearchFilter} width="100%" />
            </Grid>
          </Grid>
        }
        rightContent={
          <Grid
            container
            spacing={1}
            alignItems="center"
            justifyContent="flex-start"
            flexWrap="wrap"
          >
            <Grid item xs={12} sm="auto">
              <CustomButton
                variant="contained"
                onClick={handleClickOpen}
                startIcon={<AddIcon />}
                label={t('clientsecret.button.add')}
              />
            </Grid>
          </Grid>
        }
      />

      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="client-secret-dialog-title"
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle id="client-secret-dialog-title">
          {t('clientsecret.dialog.title')}
        </DialogTitle>
        <Divider />

        <DialogContent>
          <CardWrapper>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  {t('clientsecret.info.apikey_description')}
                </Typography>
              </Grid>

              {!generatedSecret && (
                <>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body1" fontWeight={500}>
                      {t('clientsecret.label.description')}{' '}
                      <Typography component="span" color="error">
                        *
                      </Typography>
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={8}>
                    <CustomTextField
                      required
                      fullWidth
                      placeholder={t('clientsecret.placeholder.description')}
                      inputProps={{ maxLength: 75 }}
                      name="Description"
                      value={description}
                      onChange={handleDescriptionChange}
                      error={!!errors.description}
                      helperText={errors.description}
                    />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <Typography variant="body1" fontWeight={500}>
                      {t('clientsecret.label.select_profile')}{' '}
                      <Typography component="span" color="error">
                        *
                      </Typography>
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={8}>
                    <FormControl fullWidth error={!!errors.profile}>
                      <InputLabel id="profile-select-label">
                        {t('clientsecret.label.select_profile')}
                      </InputLabel>
                      {profilesLoading ? (
                        <Backdrop open={profilesLoading}>
                          <CircularProgress size={24} sx={{ mt: 1 }} />
                        </Backdrop>
                      ) : (
                        <Select
                          labelId="profile-select-label"
                          id="profile-select"
                          value={selectedProfileId}
                          onChange={handleProfileSelect}
                          input={
                            <OutlinedInput
                              label={t('clientsecret.label.select_profile')}
                            />
                          }
                        >
                          {profiles.length === 0 ? (
                            <MenuItem disabled value="">
                              {t('clientsecret.info.no_profiles_available')}
                            </MenuItem>
                          ) : (
                            profiles.map((profile) => (
                              <MenuItem
                                key={profile.ProfileId}
                                value={profile.ProfileId ?? ''}
                              >
                                <ListItemText
                                  primary={profile.ProfileId}
                                  secondary={profile.Description}
                                />
                              </MenuItem>
                            ))
                          )}
                        </Select>
                      )}
                      {errors.profile && (
                        <FormHelperText>{errors.profile}</FormHelperText>
                      )}
                    </FormControl>
                  </Grid>
                </>
              )}

              {generatedSecret && (
                <>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body1" fontWeight={500}>
                      {t('clientsecret.label.api_key')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={8}>
                    <Typography
                      variant="body1"
                      sx={{
                        wordBreak: 'break-word',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      {generatedSecret.ClientId}
                      <CopyToClipboard
                        text={generatedSecret.ClientId ?? ''}
                        onCopy={() =>
                          successToastMessage(
                            t('clientsecret.success.apikey_copied'),
                          )
                        }
                      >
                        <IconButton
                          size="small"
                          aria-label={t('clientsecret.aria.copy_client_id')}
                        >
                          <ThemedCopyIcon fontSize="small" />
                        </IconButton>
                      </CopyToClipboard>
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <Typography variant="body1" fontWeight={500}>
                      {t('clientsecret.label.secret')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={8}>
                    <Typography
                      variant="body1"
                      sx={{
                        wordBreak: 'break-word',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      {generatedSecret.Secret}
                      <CopyToClipboard
                        text={generatedSecret.Secret ?? ''}
                        onCopy={() =>
                          successToastMessage(
                            t('clientsecret.success.secret_copied'),
                          )
                        }
                      >
                        <IconButton
                          size="small"
                          aria-label={t('clientsecret.aria.copy_secret')}
                        >
                          <ThemedCopyIcon fontSize="small" />
                        </IconButton>
                      </CopyToClipboard>
                    </Typography>
                  </Grid>
                </>
              )}
            </Grid>
          </CardWrapper>
        </DialogContent>

        <Divider />

        <DialogActions>
          {!generatedSecret && (
            <CustomButton
              onClick={handleGenerate}
              disabled={isSending || profilesLoading}
              startIcon={
                isSending ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <SaveIcon />
                )
              }
              label={t('clientsecret.button.generate')}
            />
          )}
          <CustomButton
            onClick={handleClose}
            startIcon={<Cancel />}
            label={t('clientsecret.button.cancel')}
          />
        </DialogActions>

        <Backdrop
          open={isSending}
          sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      </Dialog>
    </>
  );
}
